import fetchApplicantByStatus from "@services/api/offer-applicant/fetchApplicantByStatus/fetchApplicantByStatus";
import { Box, ThemeProvider, createTheme } from '@mui/material';
import { CandidateSidebar } from './CandidateSidebar';
import { CandidateDetailView } from './CandidateDetailView';
import { EmptyState } from './EmptyState';
import { OfferApplicantStatus } from './../../../../types/OfferApplicantStatus.interface';
import { useEffect, useState } from "react";
import { Loading } from "@components/common/Loading";
import { updateAssessedCandidateStatus } from "@services/api/offer-applicant/updateAssessedCandidateStatus/updateAssessedCandidateStatus";
import axios from "axios";
import { ApplicantLoadError } from "./ApplicantLoadError";

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0B0C0E',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export interface RecruiterDashboardProps {
  applicantStatus: OfferApplicantStatus | string;
  offerId: number;
  selectedCandidateId?: number | null;
}

export const RecruiterDashboard: React.FC<RecruiterDashboardProps> = ({ applicantStatus, offerId, selectedCandidateId: initialSelectedCandidateId }) => {
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(initialSelectedCandidateId ? String(initialSelectedCandidateId) : null);
  const [applicants, setApplicants] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statuses, setStatuses] = useState<Record<number, OfferApplicantStatus>>({});
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  const handleStatusChange = async (status: OfferApplicantStatus) => {
    if (selectedCandidateId) {
      setStatuses((prev) => ({ ...prev, [parseInt(selectedCandidateId, 10)]: status }));
      await updateAssessedCandidateStatus(parseInt(selectedCandidateId, 10), status);
    }
  };

  useEffect(() => {
    const handleFetchApplicants = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const fetchedApplicants = await fetchApplicantByStatus(offerId, applicantStatus);
        setApplicants(fetchedApplicants?.data?.items || []);
      } catch (error: unknown) {
        setApplicants([]);
        const responseMessage = axios.isAxiosError<{ message?: string }>(error)
          ? error.response?.data?.message
          : undefined;
        setFetchError(responseMessage || (error instanceof Error ? error.message : ""));
      } finally {
        setLoading(false);
      }
    };
    handleFetchApplicants();
  }, [applicantStatus, offerId]);

  const selectedCandidate = applicants.find((c) => c.id == selectedCandidateId);

  const currentRecruitmentStatus = selectedCandidateId
    ? statuses[parseInt(selectedCandidateId, 10)]
    : undefined;

  const defaultStatus = currentRecruitmentStatus
    ? (Object.keys(OfferApplicantStatus).find((key) =>
        OfferApplicantStatus[key as keyof typeof OfferApplicantStatus] === currentRecruitmentStatus
      ) ?? '')
    : (selectedCandidate?.status ?? '');

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ display: "flex", width: "100vw", height: "100vh", backgroundColor: "#0B0C0E" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
             <Loading />
          </Box>
        )}
        {!loading && (
          <>
            <CandidateSidebar
              candidates={applicants}
              selectedId={selectedCandidateId}
              onSelectCandidate={(id) => setSelectedCandidateId(id)}
            />
            <Box flex={1}>
              {fetchError !== null ? (
                <ApplicantLoadError message={fetchError} />
              ) : selectedCandidate ? (
                <CandidateDetailView 
                  defaultStatus={defaultStatus}
                  candidate={selectedCandidate}
                  onValidate={() =>handleStatusChange(OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW)}
                  onPending={() =>handleStatusChange(OfferApplicantStatus.UNDER_FINAL_REVIEW)}
                  onReject={() =>handleStatusChange(OfferApplicantStatus.REJECTED_AFTER_FINAL_REVIEW)}
                  onOfferSent={() => handleStatusChange(OfferApplicantStatus.OFFER_SENT)}
                  onContractSent={() => handleStatusChange(OfferApplicantStatus.CONTRACT_SENT)}
                  onHired={() => handleStatusChange(OfferApplicantStatus.HIRED)}
                />
              ) : (
                <EmptyState />
              )}
            </Box>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default RecruiterDashboard;